/**
 * Pure helpers for stats filter presets (no REST / i18n deps).
 */

import { v4 as uuidv4 } from 'uuid';

export const STATS_FILTER_PRESET_SCOPES = {
    ATTENDANCE_ANALYSIS: 'attendance_analysis',
    STATS_FORM: 'stats_form',
    ACCOUNTING_ANALYSIS: 'accounting_analysis',
    BOOKINGS_ANALYSIS: 'bookings_analysis',
    ENGINE_LOGS: 'engine_logs',
    EMAIL_CAMPAIGNS: 'email_campaigns',
};

/**
 * @param {unknown} config
 * @param {string} scope
 * @returns {Array<object>}
 */
export function presetsFromConfig(config, scope) {
    const list = config?.stats?.filterPresetsByScope?.[scope];
    if (!Array.isArray(list)) {
        return [];
    }
    return list.filter((p) => p && typeof p === 'object' && typeof p.id === 'string');
}

/**
 * @param {string} scope
 * @param {Array<object>} presets
 * @returns {object} patch for mergeInstanceConfig
 */
export function buildScopePresetsPatch(scope, presets) {
    return {
        stats: {
            filterPresetsByScope: {
                [scope]: Array.isArray(presets) ? presets : [],
            },
        },
    };
}

/**
 * @param {{ id?: string, name: string, scope: string, filters: object }} input
 * @returns {object}
 */
export function buildFilterPreset({ id, name, scope, filters }) {
    const trimmed = String(name || '').trim();
    return {
        id: id || uuidv4(),
        name: trimmed,
        scope,
        filters: filters && typeof filters === 'object' ? filters : {},
        updated_at: new Date().toISOString(),
    };
}

/**
 * @param {Array<object>} list
 * @param {object} preset
 * @returns {Array<object>}
 */
export function upsertPresetInList(list, preset) {
    const arr = Array.isArray(list) ? [...list] : [];
    const i = arr.findIndex((p) => p && p.id === preset.id);
    if (i === -1) {
        arr.push(preset);
    } else {
        arr[i] = preset;
    }
    return arr;
}

/**
 * @param {Array<object>} list
 * @param {string} id
 * @returns {Array<object>}
 */
export function removePresetFromList(list, id) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.filter((p) => p && p.id !== id);
}

/**
 * @param {Array<object>} list
 * @param {string} id
 * @param {string} name
 * @returns {Array<object>}
 */
export function renamePresetInList(list, id, name) {
    const trimmed = String(name || '').trim();
    if (!trimmed) {
        return Array.isArray(list) ? [...list] : [];
    }
    return (Array.isArray(list) ? list : []).map((p) => {
        if (!p || p.id !== id) {
            return p;
        }
        return { ...p, name: trimmed, updated_at: new Date().toISOString() };
    });
}

/**
 * Generic snapshot for analysis/stats filter presets.
 * Relative `date_preset` omits absolute bounds (re-resolved on apply / cron).
 *
 * @param {{
 *   date_preset?: string,
 *   date_from?: string|null,
 *   date_to?: string|null,
 *   form?: object,
 *   ui?: object,
 * }} state
 * @returns {object}
 */
export function captureAnalysisFilterPresetSnapshot(state) {
    const preset = state?.date_preset || 'custom';
    const isCustom = !preset || preset === 'custom';
    const out = {
        date_preset: preset,
        date_from: isCustom ? (state?.date_from ?? null) : null,
        date_to: isCustom ? (state?.date_to ?? null) : null,
        form: state?.form && typeof state.form === 'object' ? state.form : {},
    };
    if (state?.ui && typeof state.ui === 'object') {
        out.ui = state.ui;
    }
    return out;
}

/**
 * Snapshot of attendance analysis filters for a preset.
 *
 * @param {{
 *   date_preset: string,
 *   date_from?: string|null,
 *   date_to?: string|null,
 *   places?: string[],
 *   halls?: string[],
 *   metric?: string,
 *   pieMetric?: string,
 * }} state
 * @returns {object}
 */
export function captureAttendanceAnalysisFilters(state) {
    return captureAnalysisFilterPresetSnapshot({
        date_preset: state?.date_preset,
        date_from: state?.date_from,
        date_to: state?.date_to,
        form: {
            places: Array.isArray(state?.places) ? state.places : [],
            halls: Array.isArray(state?.halls) ? state.halls : [],
            metric: state?.metric || 'scans',
        },
        ui: {
            pieMetric: state?.pieMetric === 'minutes' ? 'minutes' : 'count',
        },
    });
}

/**
 * @param {object} filters preset.filters
 * @returns {string}
 */
export function presetDatePresetKey(filters) {
    const key = filters?.date_preset;
    return typeof key === 'string' && key ? key : 'custom';
}
